import React from 'react';
import { Observer } from 'mobx-react';
import { when } from 'mobx';

import { Section } from 'controls/react/layout/section';
import { DatasourceValueEntry } from 'controls/designer/features/widgets/textWidget/dynamic-data/datasource-value/types';
import { DatasourceValueStore } from 'controls/designer/features/widgets/textWidget/dynamic-data/datasource-value/dataSourceValueStore';
import { ApplicationState } from 'framework/applicationState';
import { DynamicDataTypeDescription, DynamicEntry } from 'controls/designer/features/widgets/textWidget/types';
import { openModal } from 'controls/react/ant/antModal';
import { FormEntryNew } from 'controls/react/form/formEntryNew';
import { DatasourceEditor } from 'controls/designer/features/widgets/datasources/shared/components/data-source-editor/datasourceEditor';
import { AntSelect } from 'controls/react/ant/antSelect';
import { apiFetch } from 'framework/api';
import { newGuid } from 'tools/guid';
import { DataSourceManager } from 'controls/designer/features/widgets/datasources/shared/utils/data-source-manager/dataSourceManager';
import { clearDatasourceCache } from 'controls/designer/features/widgets/datasources/api';
import { translator } from 'core/localization';
import { ModelValidator } from 'framework/mobx-integration';
import { Toolbar } from 'controls/react/layout/toolbar';
import { ActionButtons } from 'controls/react/form';

const i18n = translator({
	'Row': {},
	'Display column': {},
	'Datasource value': {},
});

export const dynamicValueTypeDescription: DynamicDataTypeDescription = {
	type: 'datasource-value',
	menuLabel: i18n('Datasource value'),
	getStore: (entry: DynamicEntry) => new DatasourceValueStore(entry as DatasourceValueEntry),
	getDefaultConfig: () => {
		return {
			dataSourceSelection: {
				dataSourceSelectorAccountId: null,
				accountId: ApplicationState.accountId,
			},
		};
	},
	editConfig: (entry: DynamicEntry) => {
		const datasourceEntry = entry as DatasourceValueEntry;
		const requestId = newGuid();

		const manager = new DataSourceManager({
			selection: datasourceEntry.dataSourceSelection,
			loadDatasourceList: true,
			requestId,
		});

		const validator = new ModelValidator(datasourceEntry);
		validator.required('displayColumn');

		const modal = openModal(
			{
				title: i18n('Datasource value'),
				width: 400,
				footer: null,
			},
			<Observer>
				{() => (
					<Section childrenPadding={true} hasFooter={true}>
						<DatasourceEditor manager={manager} showRowEditor={true} />
						<FormEntryNew label={i18n('Display column')} model={datasourceEntry} modelField={'displayColumn'}>
							<AntSelect options={manager.columnOptions} />
						</FormEntryNew>
						<Toolbar>
							<ActionButtons
								mode={datasourceEntry.id ? 'update' : 'create'}
								validator={validator}
								onSave={() => modal.close(true)}
								onCancel={() => modal.close(false)}
							/>
						</Toolbar>
					</Section>
				)}
			</Observer>,
		);

		return new Promise((resolve) => {
			modal.promise
				.then((resolved) => {
					if (resolved) {
						datasourceEntry.placeholder = `${manager.columnName} = ${manager.config.selection.rowKeyValue} / ${datasourceEntry.displayColumn} `;
					}
					resolve(resolved);
				})
				.catch(() => resolve(false))
				.finally(() => {
					manager.destroy();
					void apiFetch(clearDatasourceCache({ accountId: datasourceEntry.dataSourceSelection.accountId, requestId }));
				});
		});
	},
	getTooltip: async (entry: DynamicEntry) => {
		const datasourceEntry = entry as DatasourceValueEntry;
		const requestId = newGuid();

		const manager = new DataSourceManager({
			selection: datasourceEntry.dataSourceSelection,
			loadDatasourceList: true,
			requestId,
		});

		await when(() => !manager.dataSources.loading && !manager.rulesConfiguration.loading);

		const dataSourceName = manager.selectedDataSource?.name ?? '';
		const columnName = manager.columnName ?? '';
		const rowKeyValue = manager.config.selection.rowKeyValue ?? '';

		manager.destroy();
		void apiFetch(clearDatasourceCache({ accountId: datasourceEntry.dataSourceSelection.accountId, requestId }));

		return `DataSource: ${dataSourceName}\r\n${columnName} = ${rowKeyValue}`;
	},
};
